// Library methods
import { useTranslation } from "react-i18next";
import React, { useCallback, useMemo } from "react";

// MUI Components
import { Grid, Typography } from "@mui/material";

// Components
import VisualAcuityBar from "./VisualAcuityBar";

// Utilities
import styles from "./index.module.css";

const VisualAcuityGraph = ({
  examData,
  isLandoltC = null,
  isSnellen = null,
  isLiveUpdate = null,
}) => {
  const { t } = useTranslation();

  const lettersMap = useMemo(
    () =>
      isSnellen
        ? null
        : isLandoltC
          ? {
              E: "e",
              SE: "f",
              S: "g",
              SW: "h",
              W: "i",
              NW: "j",
              N: "k",
              NE: "l",
            }
          : {
              UP: "d",
              DOWN: "b",
              LEFT: "c",
              RIGHT: "a",
            },
    [isSnellen, isLandoltC]
  );

  const getChartLetters = useCallback(
    (chartRows) => {
      let displayedLetters = [];

      chartRows?.forEach((chartRow) => {
        displayedLetters.push(
          chartRow?.letters?.map((char) => ({
            color:
              char?.State === "GuessedCorrectly"
                ? "#13A321"
                : char?.State === "Skipped"
                  ? "#202338"
                  : char?.State === "None"
                    ? "#202338"
                    : "#B21515",
            backgroundImage:
              char?.State === "Skipped"
                ? "linear-gradient(0deg, #E7C43C60 25%, #FFF 25%, #FFF 50%, #E7C43C60 50%, #E7C43C60 75%, #FFF 75%, #FFF 100%)"
                : "none",
            backgroundSize: "8px 8px",
            value: lettersMap ? lettersMap[char?.Value] : char?.Value,
          }))
        );
      });
      if (
        examData?.state?.letterIndex >= 0 &&
        examData?.state?.rowIndex >= 0 &&
        displayedLetters[examData.state.rowIndex][examData.state.letterIndex]
      ) {
        displayedLetters[examData.state.rowIndex][examData.state.letterIndex][
          "color"
        ] = "#E2772E";
      }

      return displayedLetters;
    },
    [examData?.state?.rowIndex, examData?.state?.letterIndex, lettersMap]
  );

  return (
    <Grid container item xs={12} key={examData?.id}>
      <Grid item display="flex" xs={12}>
        <Grid item xs={3}>
          <Typography textAlign="center" fontSize="14px" fontWeight={600}>
            {t("snellen_equivalent")}
          </Typography>
        </Grid>
        <Grid item xs={6}></Grid>
        <Grid container item xs={3}>
          <Grid item xs={6}>
            <Typography textAlign="center" fontSize="14px" fontWeight={600}>
              {t("word_logmar")}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography textAlign="center" fontSize="14px" fontWeight={600}>
              {t("word_index")}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
      <Grid item display="flex" xs={3} mb="20px">
        <Grid item xs={6}>
          <Typography textAlign="center" fontSize="14px" fontWeight={600}>
            20 / {t("word_foot")}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography textAlign="center" fontSize="14px" fontWeight={600}>
            6 / {t("word_meter")}
          </Typography>
        </Grid>
      </Grid>
      {examData?.state?.charts?.map((chart) => {
        const chartRows = chart?.rows ?? [];
        const chartLetters = getChartLetters(chartRows);

        return (
          <Grid container item xs={12} key={chart?.index}>
            {chartLetters?.map((chartLetter, index) => (
              <Grid container item key={index}>
                {/* Snellen Equivalent values */}
                <Grid item display="flex" alignItems="center" xs={3}>
                  <Grid item xs={6} textAlign="center">
                    <Typography textAlign="center">
                      {chartRows?.[index]?.snellenVisualAcuityScaleFeet ?? "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} textAlign="center">
                    <Typography textAlign="center">
                      {chartRows?.[index]?.snellenVisualAcuityScaleMetres ??
                        "-"}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container item xs={6}>
                  <Grid
                    item
                    xs={12}
                    display="flex"
                    justifyContent="center"
                    position={
                      index === 5 || index === 8 ? "relative" : "static"
                    }
                  >
                    {chartLetter?.map((letter, j) => {
                      const value = letter?.value;
                      return (
                        <Grid
                          item
                          key={j}
                          sx={{
                            backgroundImage: letter?.backgroundImage,
                            backgroundSize: letter?.backgroundSize,
                            display: "flex",
                            justifyContent: "center",
                            height: "32px",
                            width: "32px",
                          }}
                        >
                          <Typography
                            display="inline-block"
                            fontSize="24px"
                            color={letter?.color}
                            className={
                              isSnellen
                                ? styles.snellen_chart_font
                                : styles.eye_chart_font
                            }
                          >
                            {value}
                          </Typography>
                        </Grid>
                      );
                    })}
                    {(index === 5 || index === 8) && (
                      <VisualAcuityBar
                        index={index}
                        isLiveUpdate={isLiveUpdate}
                      />
                    )}
                  </Grid>
                </Grid>
                {/* LogMAR and Index values */}
                <Grid container item display="flex" alignItems="center" xs={3}>
                  <Grid item xs={6}>
                    <Typography textAlign="center">
                      {chartRows?.[index]?.logMARVisualAcuityScale ?? "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography textAlign="center">{index + 1}</Typography>
                  </Grid>
                </Grid>
              </Grid>
            ))}
          </Grid>
        );
      })}
    </Grid>
  );
};

export default VisualAcuityGraph;
